import React from "react";
import { useDownloadableSource } from "../../resource/DownloadableSource";
import { ResourceView } from "../../../../dto/ResourceView";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { Link, Skeleton, Tooltip, Typography } from "@mui/material";
import { AvailabilitySource } from "../../../../contexts/EResourceAvailabilityContext";

interface NotAvailableTextProps {
  source: AvailabilitySource;
}
const NotAvailableText: React.FC<NotAvailableTextProps> = ({
  source: { preRelease, releaseDate, numberOfHolds, copiesOwned },
}) => {
  const { translate } = useLanguage();
  if (preRelease) {
    return (
      <Typography component={"span"}>
        {" "}
        {translate("preReleaseMessage")} {releaseDate}
      </Typography>
    );
  }

  const borrowerLabel = translate(
    numberOfHolds === 1 ? "borrower" : "borrowers"
  );
  const copyLabel = translate(copiesOwned === 1 ? "copy" : "copies");

  return (
    <Typography component={"span"}>
      {" "}
      {numberOfHolds} {borrowerLabel} {translate("availability_waitingOn")}{" "}
      {copiesOwned} {copyLabel}
    </Typography>
  );
};

interface DownloadableItemAvailabilityProps {
  resource: ResourceView;
  dense?: boolean;
}
export const DownloadableItemAvailability = ({
  resource,
  dense = false,
}: DownloadableItemAvailabilityProps) => {
  const { source, loading, hasDownloadbleCheckouts } =
    useDownloadableSource(resource);
  const { translate } = useLanguage();

  if (loading) {
    return <Skeleton variant="text" />;
  }

  return (
    <Link
      href={source?.downloadLink ?? resource.link}
      target={"_blank"}
      rel="noopener noreferrer"
    >
      {source ? (
        <>
          {source.alwaysAvailable ? (
            <Typography
              component={"span"}
              color={"success"}
              fontWeight={"bold"}
            >
              {translate("alwaysAvailable_download")}
            </Typography>
          ) : !source.availability ? (
            dense ? (
              <Tooltip title={<NotAvailableText source={source} />}>
                <Typography
                  component={"span"}
                  color={"error"}
                  fontWeight={"bold"}
                >
                  {translate("notAvailable_download")}
                </Typography>
              </Tooltip>
            ) : (
              <>
                <Typography
                  component={"span"}
                  color={"error"}
                  fontWeight={"bold"}
                >
                  {translate("notAvailable_download")}
                </Typography>
                <NotAvailableText source={source} />
                {!hasDownloadbleCheckouts && (
                  <>
                    {" ("}
                    {translate("checkDigitalLocations")}
                    {")"}
                  </>
                )}
              </>
            )
          ) : (
            <Typography
              component={"span"}
              color={"success"}
              fontWeight={"bold"}
            >
              {source.copiesAvailable} {translate("available_download")}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography fontWeight="bold" component={"span"} color="info">
            {translate("downloadable")}
          </Typography>
        </>
      )}
    </Link>
  );
};
